import React, { useState, useMemo, useEffect } from 'react';
import { useLanguage } from '@/shared/hooks/useLanguage';
import { useTheme } from '@/app/contexts/ThemeContext';
import Header from '@/shared/components/layout/Header';
import SEOHead from '@/shared/components/common/SEOHead';
import { PricingPlan, OrderSummary, PaymentMethod } from '../types/pricing.types';
import OrderSummaryModal from './OrderSummaryModal';
import PaymentModal from './PaymentModal';

const PricingPage: React.FC = () => {
  const { t, isRTL, language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);

  // Define pricing plans
  const plans: PricingPlan[] = useMemo(() => [
    {
      id: 'free',
      name: t('pricing.free'),
      price: 0,
      period: 'month',
      features: [
        { id: 'keywordExtraction', name: t('pricing.features.keywordExtraction'), included: true },
        { id: 'singleUser', name: t('pricing.features.singleUser'), included: true },
        { id: 'searchVolume', name: t('pricing.features.searchVolume'), included: true },
        { id: 'keywordDifficulty', name: t('pricing.features.keywordDifficulty'), included: true },
        { id: 'freeTrial', name: t('pricing.features.freeTrial'), included: true },
        { id: 'moreKeywordExtraction', name: t('pricing.features.moreKeywordExtraction'), included: false },
        { id: 'excelExport', name: t('pricing.features.excelExport'), included: false },
        { id: 'support24h', name: t('pricing.features.support24h'), included: false },
      ],
    },
    {
      id: 'monthly',
      name: t('pricing.monthly'),
      price: 150000,
      originalPrice: 150000,
      period: 'month',
      features: [
        { id: 'keywordExtraction', name: t('pricing.features.keywordExtraction'), included: true },
        { id: 'singleUser', name: t('pricing.features.singleUser'), included: true },
        { id: 'searchVolume', name: t('pricing.features.searchVolume'), included: true },
        { id: 'keywordDifficulty', name: t('pricing.features.keywordDifficulty'), included: true },
        { id: 'moreKeywordExtraction', name: t('pricing.features.moreKeywordExtraction'), included: true },
        { id: 'excelExport', name: t('pricing.features.excelExport'), included: true },
        { id: 'support24h', name: t('pricing.features.support24h'), included: true },
        { id: 'freeTrial', name: t('pricing.features.freeTrial'), included: false },
      ],
    },
    {
      id: 'yearly',
      name: t('pricing.yearly'),
      price: 900000,
      originalPrice: 1800000, // 150000 * 12
      period: 'year',
      discount: 50,
      popular: true,
      features: [
        { id: 'keywordExtraction', name: t('pricing.features.keywordExtraction'), included: true },
        { id: 'singleUser', name: t('pricing.features.singleUser'), included: true },
        { id: 'searchVolume', name: t('pricing.features.searchVolume'), included: true },
        { id: 'keywordDifficulty', name: t('pricing.features.keywordDifficulty'), included: true },
        { id: 'moreKeywordExtraction', name: t('pricing.features.moreKeywordExtraction'), included: true },
        { id: 'excelExport', name: t('pricing.features.excelExport'), included: true },
        { id: 'support24h', name: t('pricing.features.support24h'), included: true },
        { id: 'freeTrial', name: t('pricing.features.freeTrial'), included: false },
      ],
    },
  ], [t]);

  // Calculate order summary
  const orderSummary: OrderSummary | null = useMemo(() => {
    if (!selectedPlan) return null;

    const subtotal = selectedPlan.price;
    const discount = selectedPlan.originalPrice 
      ? selectedPlan.originalPrice - selectedPlan.price 
      : 0;
    const tax = subtotal > 0 ? Math.round(subtotal * 0.09) : 0; // 9% VAT
    const total = subtotal + tax;

    return {
      plan: selectedPlan,
      subtotal,
      discount,
      tax,
      total,
    };
  }, [selectedPlan]);

  const handlePlanSelect = (plan: PricingPlan) => {
    setSelectedPlan(plan);
    setPaymentMethod(null);
    setShowOrderSummaryModal(true);
  };

  const handlePayment = (method: PaymentMethod) => {
    setPaymentMethod(method);
    setShowOrderSummaryModal(false);
    setShowPaymentModal(true);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fa-IR').format(price);
  };

  // Update document direction
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language === 'fa' ? 'fa' : 'en';
  }, [isRTL, language]);

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <SEOHead />
      <Header />
      <div className="py-12 px-4">
        <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('pricing.title')}
          </h1>
          <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" dir={isRTL ? 'rtl' : 'ltr'}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl shadow-lg transition-all duration-300 ${
                plan.popular
                  ? 'ring-4 ring-primary scale-105'
                  : 'hover:scale-105'
              } ${
                isDark
                  ? 'bg-gray-800 border border-gray-700'
                  : 'bg-white border border-gray-200'
              }`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2" dir={isRTL ? 'rtl' : 'ltr'}>
                  <span className="bg-primary text-white px-4 py-1 rounded-full text-sm font-semibold">
                    {t('pricing.mostPopular')}
                  </span>
                </div>
              )}

              {plan.discount && (
                <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {plan.discount}% {t('pricing.discount')}
                  </span>
                </div>
              )}

              <div className="p-8">
                {/* Plan Name */}
                <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>

                {/* Price */}
                <div className="mb-6">
                  {plan.originalPrice && plan.originalPrice > plan.price && (
                    <p className={`text-sm line-through mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {formatPrice(plan.originalPrice)} {t('pricing.toman')}
                    </p>
                  )}
                  <p className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {plan.price === 0 ? (
                      t('pricing.free')
                    ) : (
                      <>
                        {formatPrice(plan.price)} {t('pricing.toman')}
                        <span className="text-lg font-normal">
                          {' '}/ {plan.period === 'month' ? t('pricing.month') : t('pricing.year')}
                        </span>
                      </>
                    )}
                  </p>
                </div>

                {/* Features */}
                <div className="mb-6">
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {t('pricing.features.title')}
                  </h4>
                  <ul className="space-y-2">
                    {plan.features.map((feature) => (
                      <li key={feature.id} className="flex items-center">
                        {feature.included ? (
                          <svg
                            className="w-5 h-5 text-green-500 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        ) : (
                          <svg
                            className={`w-5 h-5 text-gray-400 flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-2'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        )}
                        <span
                          className={`text-sm ${
                            feature.included
                              ? isDark
                                ? 'text-gray-300'
                                : 'text-gray-700'
                              : isDark
                              ? 'text-gray-600'
                              : 'text-gray-400'
                          }`}
                        >
                          {feature.name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Select Button */}
                <button
                  onClick={() => handlePlanSelect(plan)}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                    plan.id === 'free'
                      ? isDark
                        ? 'bg-gray-700 text-white hover:bg-gray-600'
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                >
                  {plan.id === 'free' ? t('pricing.selectPlan') : t('pricing.selectPlan')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary Modal */}
        {showOrderSummaryModal && orderSummary && (
          <OrderSummaryModal
            isOpen={showOrderSummaryModal}
            onClose={() => {
              setShowOrderSummaryModal(false);
              setSelectedPlan(null);
            }}
            orderSummary={orderSummary}
            onPayment={handlePayment}
            formatPrice={formatPrice}
          />
        )}

        {/* Payment Modal */}
        {showPaymentModal && selectedPlan && paymentMethod && (
          <PaymentModal
            isOpen={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setPaymentMethod(null);
            }}
            plan={selectedPlan}
            orderSummary={orderSummary!}
            paymentMethod={paymentMethod}
            formatPrice={formatPrice}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;

